import { useEffect, useState } from "react";
import {
  AI_PROVIDER_OPTIONS,
  AI_PROVIDERS,
  DEFAULT_AI_PROVIDER,
  type AiProvider,
  buildCaloriePrompt,
  getStoredAiProvider,
  setStoredAiProvider,
} from "../lib/ai";

export function AiKcalButton({
  foodName,
  buttonLabel = "AI㌍",
  compact = false,
}: {
  foodName: string;
  buttonLabel?: string;
  compact?: boolean;
}) {
  const [provider, setProvider] = useState<AiProvider>(
    getStoredAiProvider() ?? DEFAULT_AI_PROVIDER,
  );
  const [draftProvider, setDraftProvider] = useState<AiProvider>(
    getStoredAiProvider() ?? DEFAULT_AI_PROVIDER,
  );
  const [chooserOpen, setChooserOpen] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    const storedProvider = getStoredAiProvider() ?? DEFAULT_AI_PROVIDER;
    setProvider(storedProvider);
    setDraftProvider(storedProvider);
  }, []);

  const trimmedFoodName = foodName.trim();
  const disabled = false;

  const launch = async (nextProvider: AiProvider) => {
    const prompt = buildCaloriePrompt(trimmedFoodName);
    const config = AI_PROVIDERS[nextProvider];

    try {
      await navigator.clipboard.writeText(prompt);
      setStatus(
        config.supportsPromptParam
          ? "質問文をコピーして外部AIを開きました。"
          : "Gemini は質問文をコピーしてから開きます。貼り付けて使ってください。",
      );
    } catch {
      setStatus(
        config.supportsPromptParam
          ? "外部AIを開きました。"
          : "Gemini はURLで質問文を渡せないため、手動で入力してください。",
      );
    }

    window.open(config.buildUrl(prompt), "_blank", "noopener,noreferrer");
  };

  const openChooser = () => {
    setStatus(null);
    setDraftProvider(provider);
    setChooserOpen(true);
  };

  const saveProviderSelection = () => {
    setStoredAiProvider(draftProvider);
    setProvider(draftProvider);
    setChooserOpen(false);
    setStatus(
      `「AI㌍」の呼び出し先を ${AI_PROVIDERS[draftProvider].label} に変更しました。`,
    );
  };

  return (
    <div className={`ai-kcal-box${compact ? " ai-kcal-box-compact" : ""}`}>
      <div className="ai-kcal-actions">
        <button
          type="button"
          className={compact ? "ai-kcal-compact-button" : "btn-pearl"}
          disabled={disabled}
          onClick={() => {
            setStatus(null);
            void launch(provider);
          }}
        >
          {buttonLabel}
        </button>
        {!compact && (
          <button type="button" className="btn-link" onClick={openChooser}>
            AI変更
          </button>
        )}
      </div>

      {!compact && (
        <p className="ai-kcal-note">現在: {AI_PROVIDERS[provider].label}</p>
      )}
      {!compact && status && <p className="ai-kcal-note">{status}</p>}

      {chooserOpen && (
        <div className="modal-backdrop" role="presentation">
          <div
            className="modal-card ai-provider-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-provider-title"
          >
            <h2 id="ai-provider-title" className="modal-title">
              AIを選ぶ
            </h2>
            <p className="muted" style={{ marginTop: 0 }}>
              「AI㌍」で呼び出すAIを選択
            </p>
            <div className="ai-provider-list">
              {AI_PROVIDER_OPTIONS.map((candidate) => (
                  <button
                    key={candidate}
                    type="button"
                    className={`ai-provider-button${draftProvider === candidate ? " is-selected" : ""}`}
                    aria-pressed={draftProvider === candidate}
                    onClick={() => {
                      setDraftProvider(candidate);
                    }}
                  >
                    {AI_PROVIDERS[candidate].label}
                  </button>
                ))}
            </div>
            <div className="camera-modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setChooserOpen(false)}
              >
                閉じる
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={saveProviderSelection}
              >
                変更する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
